/**
 * ShareModalSearch — user search input with autocomplete dropdown for the sharing modal.
 *
 * Queries GET /v1/users/search with debounce (300ms, min 2 chars) and presents
 * results in a Radix UI Popover dropdown. Filters out existing board members
 * client-side via the `excludeUserIds` prop.
 *
 * Supports keyboard navigation (arrow keys, Enter to select, Escape to dismiss)
 * and follows the combobox ARIA pattern.
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 3.10, 10.2, 10.3
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import * as api from '../../services/api-client';
import { filterExistingMembers } from './sharing-types';
import type { SelectedUser } from './sharing-types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserSearchResult {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
}

interface SearchResponse {
  data: UserSearchResult[];
}

export interface ShareModalSearchProps {
  readonly excludeUserIds: readonly string[];
  readonly onSelect: (user: SelectedUser) => void;
  readonly placeholder?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShareModalSearch({
  excludeUserIds,
  onSelect,
  placeholder = 'Add people by name or email…',
}: ShareModalSearchProps): React.ReactElement {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [hasSearched, setHasSearched] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filter results to exclude existing members
  const filteredResults = filterExistingMembers(results, excludeUserIds);

  // Show "No users found" message when search returned results but all are filtered
  // or when no results came back at all
  const showNoResults = hasSearched && !isLoading && query.length >= 2 && filteredResults.length === 0;

  // Show email invite option when query is a valid email and no user match exists
  const showEmailFallback =
    query.length >= 2 &&
    isValidEmail(query) &&
    !filteredResults.some((r) => r.email === query);

  // Total selectable items
  const totalItems = filteredResults.length + (showEmailFallback ? 1 : 0);

  // Should the popover be open
  const popoverOpen = isOpen && (totalItems > 0 || showNoResults);

  // ---------------------------
  // Debounced search
  // ---------------------------

  React.useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      setActiveIndex(-1);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await api.get<SearchResponse>('/v1/users/search', { q: query });
        setResults(response.data);
        setIsOpen(true);
        setActiveIndex(-1);
        setHasSearched(true);
      } catch {
        setResults([]);
        setHasSearched(true);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // ---------------------------
  // Selection handlers
  // ---------------------------

  function handleSelectUser(user: UserSearchResult): void {
    onSelect({ userId: user.userId, displayName: user.displayName, email: user.email });
    resetState();
  }

  function handleSelectEmail(): void {
    if (isValidEmail(query)) {
      onSelect({ email: query });
      resetState();
    }
  }

  function handleSelectActive(): void {
    if (activeIndex < 0) {
      if (showEmailFallback) {
        handleSelectEmail();
      }
      return;
    }

    const selectedResult = filteredResults[activeIndex];
    if (activeIndex < filteredResults.length && selectedResult) {
      handleSelectUser(selectedResult);
    } else if (showEmailFallback) {
      handleSelectEmail();
    }
  }

  function resetState(): void {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setActiveIndex(-1);
    setHasSearched(false);
  }

  // ---------------------------
  // Keyboard navigation
  // ---------------------------

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (!isOpen && totalItems === 0) return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (totalItems > 0) {
          setIsOpen(true);
          setActiveIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        }
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (totalItems > 0) {
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        }
        break;
      }
      case 'Enter': {
        event.preventDefault();
        handleSelectActive();
        break;
      }
      case 'Escape': {
        event.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      }
    }
  }

  // ---------------------------
  // Scroll active item into view
  // ---------------------------

  React.useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll<HTMLElement>('[role="option"]');
    const activeItem = items[activeIndex];
    if (activeItem && typeof activeItem.scrollIntoView === 'function') {
      activeItem.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // ---------------------------
  // Render
  // ---------------------------

  const listboxId = 'share-modal-search-listbox';

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={popoverOpen}
        aria-controls={listboxId}
        aria-activedescendant={
          activeIndex >= 0 ? `share-modal-search-option-${activeIndex}` : undefined
        }
        aria-autocomplete="list"
        aria-label="Search for users to invite"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (totalItems > 0 || showNoResults) setIsOpen(true);
        }}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
          'ring-offset-background placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      />
      {isLoading && (
        <span
          className="absolute right-3 top-1/2 -translate-y-1/2"
          aria-live="polite"
          aria-label="Loading search results"
        >
          <LoadingSpinner />
        </span>
      )}

      {popoverOpen && (
        <ul
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label="User search results"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {filteredResults.map((user, index) => (
            <li
              key={user.userId}
              id={`share-modal-search-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={cn(
                'flex cursor-pointer select-none flex-col rounded-sm px-3 py-2 text-sm',
                'transition-colors',
                index === activeIndex
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent hover:text-accent-foreground',
              )}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectUser(user)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <span className="font-medium">{user.displayName}</span>
              <span className="text-xs text-muted-foreground">{user.email}</span>
            </li>
          ))}

          {showNoResults && !showEmailFallback && (
            <li className="px-3 py-2 text-sm text-muted-foreground" aria-disabled="true">
              No users found
            </li>
          )}

          {showEmailFallback && (
            <li
              id={`share-modal-search-option-${filteredResults.length}`}
              role="option"
              aria-selected={activeIndex === filteredResults.length}
              className={cn(
                'flex cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-2 text-sm',
                'transition-colors',
                activeIndex === filteredResults.length
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent hover:text-accent-foreground',
              )}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSelectEmail}
              onMouseEnter={() => setActiveIndex(filteredResults.length)}
            >
              <span className="text-muted-foreground">Invite by email:</span>
              <span className="font-medium">{query}</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LoadingSpinner — small animated spinner
// ---------------------------------------------------------------------------

function LoadingSpinner(): React.ReactElement {
  return (
    <svg
      className="h-4 w-4 animate-spin text-muted-foreground"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
